import AddIcon from '@material-design-icons/svg/filled/add.svg?react';
import ContentCopyIcon from '@material-design-icons/svg/filled/content_copy.svg?react';
import KeyIcon from '@material-design-icons/svg/filled/key.svg?react';
import LockIcon from '@material-design-icons/svg/filled/lock.svg?react';
import { uniq } from 'es-toolkit/array';
import { isLeft, isRight } from 'fp-ts/Either';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { useAsyncData } from '../hooks/useAsyncData';
import { useTRPC } from '../hooks/useTRPC';
import { usePageTitle } from '../hooks/usePageTitle';
import { useRequireAuth } from '../hooks/useRequireAuth';
import { cn } from '../lib/utils';
import { captureAndReportError } from '../utils/errorReporting';
import { normalizeMetricKey } from '../utils/sentryMetrics';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

type MetricsIngestionKey = {
  id: string;
  source: string;
  label: string;
  description: string | null;
  keyPrefix: string;
  allowedTypes: string[];
  created: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
};

function formatDate(value: string | null): string {
  if (!value) {
    return 'Never';
  }

  return new Date(value).toLocaleString();
}

function parseAllowedTypes(value: string): string[] {
  return uniq(
    value
      .split(',')
      .map((entry) => normalizeMetricKey(entry.trim()))
      .filter((entry) => entry !== 'unknown'),
  );
}

function SecretBanner({
  secret,
  onDismiss,
}: {
  secret: string;
  onDismiss: () => void;
}) {
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      toast.success('Secret copied to clipboard');
    } catch {
      toast.error('Could not copy secret');
    }
  };

  return (
    <div className="rounded-2xl border border-amber-500/25 bg-amber-950/30">
      <div className="flex flex-col gap-3 border-b border-amber-500/15 px-5 py-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-2.5">
          <LockIcon className="size-4 fill-amber-300" />
          <div>
            <p className="text-sm font-medium text-amber-100">
              Save the secret now
            </p>
            <p className="text-xs text-amber-200/70">
              It is only shown once after creation.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void handleCopy()}
            className="text-amber-100 hover:bg-amber-500/10 hover:text-amber-50"
          >
            <ContentCopyIcon className="size-3.5 fill-current" />
            Copy Secret
          </Button>
          <button
            type="button"
            onClick={onDismiss}
            className="rounded-md px-2.5 py-1 text-xs text-amber-300/70 transition-colors hover:text-amber-200"
          >
            Dismiss
          </button>
        </div>
      </div>
      <div className="px-5 py-4">
        <code className="block break-all rounded-xl bg-black/30 px-4 py-3 font-mono text-[13px] leading-relaxed text-amber-50">
          {secret}
        </code>
      </div>
    </div>
  );
}

function KeyRow({
  item,
  onRevoke,
}: {
  item: MetricsIngestionKey;
  onRevoke: (item: MetricsIngestionKey) => void;
}) {
  const isRevoked = Boolean(item.revokedAt);

  return (
    <div className="grid gap-4 px-5 py-4 md:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1fr)_auto] md:items-start">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium text-neutral-100">
            {item.label}
          </span>
          <span
            className={cn(
              'inline-flex rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide',
              isRevoked
                ? 'border-neutral-700 bg-neutral-800 text-neutral-400'
                : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300',
            )}
          >
            {isRevoked ? 'Revoked' : 'Active'}
          </span>
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-neutral-400">
          <code className="rounded bg-neutral-900 px-2 py-0.5 text-neutral-300">
            {item.source}
          </code>
          <code className="rounded bg-neutral-900 px-2 py-0.5 text-neutral-500">
            {item.keyPrefix}...
          </code>
        </div>
        {item.description && (
          <p className="mt-2 text-xs text-neutral-500">{item.description}</p>
        )}
      </div>

      <div>
        <div className="text-[11px] font-medium uppercase tracking-wide text-neutral-500">
          Allowed Types
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {item.allowedTypes.length > 0 ? (
            item.allowedTypes.map((type) => (
              <code
                key={type}
                className="rounded bg-neutral-900 px-2 py-1 text-[11px] text-neutral-300"
              >
                {type}
              </code>
            ))
          ) : (
            <span className="text-xs text-neutral-500">All types</span>
          )}
        </div>
      </div>

      <div className="space-y-1 text-xs text-neutral-400">
        <div>Created {formatDate(item.created)}</div>
        <div>
          Last used {item.lastUsedAt ? formatDate(item.lastUsedAt) : 'Never'}
        </div>
        {item.revokedAt && <div>Revoked {formatDate(item.revokedAt)}</div>}
      </div>

      <div className="flex justify-end">
        <Button
          variant="outline"
          size="sm"
          disabled={isRevoked}
          onClick={() => onRevoke(item)}
          className="border-red-500/30 bg-red-500/5 text-red-300 hover:bg-red-500/10 hover:text-red-200"
        >
          Revoke
        </Button>
      </div>
    </div>
  );
}

export function MetricsIngestionAdminPage() {
  const { authState, canRender } = useRequireAuth({
    requireRole: 'superadmin',
  });
  const trpc = useTRPC();

  usePageTitle('Metrics Ingestion | Super Admin');

  const {
    data: keys,
    isLoading,
    error: pageError,
    run,
  } = useAsyncData<MetricsIngestionKey[]>({
    initialData: [],
    initialLoading: true,
  });
  const [isCreating, setIsCreating] = useState(false);
  const [secret, setSecret] = useState<string | null>(null);
  const [revokingKey, setRevokingKey] = useState<MetricsIngestionKey | null>(
    null,
  );
  const [source, setSource] = useState('');
  const [label, setLabel] = useState('');
  const [description, setDescription] = useState('');
  const [allowedTypesInput, setAllowedTypesInput] = useState('');

  const normalizedSource = useMemo(
    () => (source.trim() ? normalizeMetricKey(source.trim()) : ''),
    [source],
  );
  const normalizedAllowedTypes = useMemo(
    () => parseAllowedTypes(allowedTypesInput),
    [allowedTypesInput],
  );

  const loadKeys = useCallback(async () => {
    await run(() => trpc.listMetricsIngestionKeys.query({}), {
      onSuccess: (result, helpers) => {
        if (isRight(result)) {
          helpers.setData(result.right.keys);
        } else {
          helpers.setError(result.left.message);
          toast.error(result.left.message);
        }
      },
      onError: (error, helpers) => {
        captureAndReportError(error, {
          operation: 'superadmin.listMetricsIngestionKeys',
          component: 'MetricsIngestionAdminPage',
        });
        helpers.setError('Failed to load metrics ingestion keys');
        toast.error('Failed to load metrics ingestion keys');
      },
    });
  }, [run, trpc]);

  useEffect(() => {
    void loadKeys();
  }, [loadKeys]);

  const handleCreate = useCallback(async () => {
    const trimmedSource = source.trim();
    const trimmedLabel = label.trim();

    if (!trimmedSource) {
      toast.error('Enter a source for the service');
      return;
    }

    if (!trimmedLabel) {
      toast.error('Enter a label for the key');
      return;
    }

    setIsCreating(true);

    try {
      const result = await trpc.createMetricsIngestionKey.mutate({
        source: trimmedSource,
        label: trimmedLabel,
        description: description.trim() || undefined,
        allowedTypes: normalizedAllowedTypes,
      });

      if (isLeft(result)) {
        toast.error(result.left.message);
        return;
      }

      setSecret(result.right.secret);
      setSource('');
      setLabel('');
      setDescription('');
      setAllowedTypesInput('');

      try {
        await navigator.clipboard.writeText(result.right.secret);
        toast.success('Metrics ingestion key created and copied');
      } catch {
        toast.success('Metrics ingestion key created');
      }

      await loadKeys();
    } catch (error) {
      captureAndReportError(error, {
        operation: 'superadmin.createMetricsIngestionKey',
        component: 'MetricsIngestionAdminPage',
        source: trimmedSource,
      });
      toast.error('Failed to create metrics ingestion key');
    } finally {
      setIsCreating(false);
    }
  }, [
    allowedTypesInput,
    description,
    label,
    loadKeys,
    normalizedAllowedTypes,
    source,
    trpc,
  ]);

  const handleRevoke = useCallback(
    async (item: MetricsIngestionKey) => {
      try {
        const result = await trpc.revokeMetricsIngestionKey.mutate({
          keyId: item.id,
        });

        if (isLeft(result)) {
          toast.error(result.left.message);
          return;
        }

        toast.success(`Revoked "${item.label}"`);
        setRevokingKey(null);
        await loadKeys();
      } catch (error) {
        captureAndReportError(error, {
          operation: 'superadmin.revokeMetricsIngestionKey',
          component: 'MetricsIngestionAdminPage',
          keyId: item.id,
        });
        toast.error('Failed to revoke metrics ingestion key');
      }
    },
    [loadKeys, trpc],
  );

  if (!canRender || authState.type !== 'Authenticated') {
    return null;
  }

  return (
    <>
      <div className="flex-1 overflow-y-auto p-4 md:p-8">
        <div className="mx-auto max-w-6xl space-y-6">
          <div className="rounded-2xl border border-neutral-700 bg-neutral-800/50 p-6">
            <div className="flex items-start gap-3">
              <KeyIcon className="mt-0.5 size-5 fill-current text-white" />
              <div className="space-y-2">
                <h2 className="text-lg font-semibold text-white">
                  Metrics Ingestion Setup
                </h2>
                <p className="max-w-3xl text-sm text-neutral-400">
                  Create one key per external service. New ingestion types
                  auto-register on first use, so you do not need a new key for
                  every payload type.
                </p>
                <div className="flex flex-wrap gap-2 text-xs text-neutral-500">
                  <span className="rounded-full border border-neutral-700 px-3 py-1">
                    `x-api-key` auth
                  </span>
                  <span className="rounded-full border border-neutral-700 px-3 py-1">
                    Source scoped
                  </span>
                  <span className="rounded-full border border-neutral-700 px-3 py-1">
                    Type auto-registration
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-neutral-700 bg-neutral-800/50">
            <div className="border-b border-neutral-700 px-6 py-5">
              <h3 className="text-lg font-semibold text-white">
                Create Ingestion Key
              </h3>
              <p className="mt-2 text-sm text-neutral-400">
                The portal will generate the secret and insert the hashed values
                directly into the database.
              </p>
            </div>

            <div className="space-y-6 p-6">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <label
                    htmlFor="metrics-ingestion-source"
                    className="text-sm font-medium text-neutral-300"
                  >
                    Source
                  </label>
                  <input
                    id="metrics-ingestion-source"
                    aria-label="Source"
                    value={source}
                    onChange={(event) => setSource(event.target.value)}
                    placeholder="catalog-service"
                    className="h-11 w-full rounded-xl border border-neutral-700 bg-neutral-900/50 px-4 text-sm text-white placeholder:text-neutral-500 focus:border-teal-500/60 focus:outline-none"
                  />
                  <p className="text-xs text-neutral-500">
                    Stored as{' '}
                    <code className="rounded bg-neutral-900 px-1.5 py-0.5 text-neutral-300">
                      {normalizedSource || 'catalog_service'}
                    </code>
                  </p>
                </div>

                <div className="space-y-2">
                  <label
                    htmlFor="metrics-ingestion-label"
                    className="text-sm font-medium text-neutral-300"
                  >
                    Label
                  </label>
                  <input
                    id="metrics-ingestion-label"
                    aria-label="Label"
                    value={label}
                    onChange={(event) => setLabel(event.target.value)}
                    placeholder="Catalog Production"
                    className="h-11 w-full rounded-xl border border-neutral-700 bg-neutral-900/50 px-4 text-sm text-white placeholder:text-neutral-500 focus:border-teal-500/60 focus:outline-none"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="metrics-ingestion-description"
                  className="text-sm font-medium text-neutral-300"
                >
                  Description
                </label>
                <textarea
                  id="metrics-ingestion-description"
                  aria-label="Description"
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  placeholder="Optional note about what this key is used for"
                  rows={3}
                  className="w-full rounded-xl border border-neutral-700 bg-neutral-900/50 px-4 py-3 text-sm text-white placeholder:text-neutral-500 focus:border-teal-500/60 focus:outline-none"
                />
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="metrics-ingestion-allowed-types"
                  className="text-sm font-medium text-neutral-300"
                >
                  Allowed Types
                </label>
                <input
                  id="metrics-ingestion-allowed-types"
                  aria-label="Allowed Types"
                  value={allowedTypesInput}
                  onChange={(event) => setAllowedTypesInput(event.target.value)}
                  placeholder="new_apps, deployment_event"
                  className="h-11 w-full rounded-xl border border-neutral-700 bg-neutral-900/50 px-4 text-sm text-white placeholder:text-neutral-500 focus:border-teal-500/60 focus:outline-none"
                />
                <p className="text-xs text-neutral-500">
                  Leave blank to allow all types for this source. Normalized
                  types:{' '}
                  {normalizedAllowedTypes.length > 0 ? (
                    normalizedAllowedTypes.map((type) => (
                      <code
                        key={type}
                        className="mr-1 inline-block rounded bg-neutral-900 px-1.5 py-0.5 text-neutral-300"
                      >
                        {type}
                      </code>
                    ))
                  ) : (
                    <span className="text-neutral-400">all types</span>
                  )}
                </p>
              </div>

              <div className="flex items-center justify-end">
                <Button
                  onClick={() => void handleCreate()}
                  disabled={isCreating}
                  size="lg"
                  className="bg-teal-600 text-white hover:bg-teal-500"
                >
                  <AddIcon className="size-4 fill-current" />
                  {isCreating ? 'Creating...' : 'Create Key'}
                </Button>
              </div>
            </div>
          </div>

          {secret && (
            <SecretBanner secret={secret} onDismiss={() => setSecret(null)} />
          )}

          <div className="rounded-2xl border border-neutral-700 bg-neutral-800/50">
            <div className="border-b border-neutral-700 px-6 py-5">
              <h3 className="text-lg font-semibold text-white">
                Existing Keys
              </h3>
              <p className="mt-2 text-sm text-neutral-400">
                Manage active and revoked ingestion credentials for external
                services.
              </p>
            </div>

            {pageError && (
              <div className="border-b border-red-500/20 bg-red-500/10 px-6 py-4 text-sm text-red-200">
                {pageError}
              </div>
            )}

            <div className="divide-y divide-neutral-700/80">
              {isLoading ? (
                <div className="flex items-center justify-center px-6 py-16">
                  <div className="size-5 animate-spin rounded-full border-2 border-neutral-700 border-t-neutral-300" />
                </div>
              ) : keys.length === 0 ? (
                <div className="px-6 py-16 text-center">
                  <p className="text-sm text-neutral-400">
                    No ingestion keys have been created yet.
                  </p>
                </div>
              ) : (
                keys.map((item) => (
                  <KeyRow key={item.id} item={item} onRevoke={setRevokingKey} />
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      <Dialog
        open={revokingKey !== null}
        onOpenChange={(open) => {
          if (!open) {
            setRevokingKey(null);
          }
        }}
      >
        <DialogContent className="border-neutral-800 bg-neutral-900 text-white">
          <DialogHeader>
            <DialogTitle>Revoke ingestion key?</DialogTitle>
            <DialogDescription className="text-neutral-400">
              This key will stop working immediately for{' '}
              <code className="mx-1 rounded bg-neutral-800 px-1.5 py-0.5 text-neutral-200">
                {revokingKey?.source}
              </code>
              and cannot be restored.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button
              variant="destructive"
              onClick={() => revokingKey && void handleRevoke(revokingKey)}
            >
              Revoke Key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
